import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import io from 'socket.io-client';
import './Messages.css';
import VoiceRecorder from '../components/chat/VoiceRecorder';
import VoicePlayer from '../components/chat/VoicePlayer';
import CreateGroupModal from '../components/chat/CreateGroupModal';
import GroupManagement from '../components/chat/GroupManagement';
import ConversationLabels from '../components/chat/ConversationLabels';
import LinkPreview from '../components/chat/LinkPreview';
import ScheduledMessagePicker from '../components/chat/ScheduledMessagePicker';
import MentionAutocomplete from '../components/chat/MentionAutocomplete';

const Messages = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchParams] = useSearchParams();
  const [socket, setSocket] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [messageMenuOpen, setMessageMenuOpen] = useState(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showConversationsList, setShowConversationsList] = useState(true);
  
  // New feature states
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showGroupManagement, setShowGroupManagement] = useState(false);
  const [showLabelsModal, setShowLabelsModal] = useState(null);
  const [showScheduledPicker, setShowScheduledPicker] = useState(false);
  const [linkPreviews, setLinkPreviews] = useState({});
  const [selectedLabelFilter, setSelectedLabelFilter] = useState(null); // null means 'All'
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const socketListenersRef = useRef({});

  // Common emojis for quick reactions
  const commonEmojis = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👏'];

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      // On desktop, always show conversations list
      if (!mobile) {
        setShowConversationsList(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Socket.io connection with proper cleanup
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const token = localStorage.getItem('token');
    const newSocket = io(process.env.NODE_ENV === 'production' 
      ? 'https://gamersunion.onrender.com' 
      : 'http://localhost:5000', {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    // Store socket event handlers for cleanup
    const handlers = {
      connect: () => console.log('Socket connected'),
      disconnect: () => console.log('Socket disconnected'),
      connect_error: (error) => console.error('Socket connection error:', error),
      
      user_online: ({ userId }) => {
        setOnlineUsers(prev => new Set([...prev, userId]));
      },
      
      user_offline: ({ userId }) => {
        setOnlineUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(userId);
          return newSet;
        });
      },
      
      new_message: ({ message }) => {
        setMessages(prev => {
          // Prevent duplicates
          if (prev.some(m => m._id === message._id)) return prev;
          return [...prev, message];
        });
        
        setTimeout(() => scrollToBottom(true), 100);
        
        // Show browser notification if not in focus
        if (document.hidden && Notification.permission === 'granted') {
          new Notification(`New message from ${message.senderId.username}`, {
            body: message.content,
            icon: message.senderId.profile?.avatar?.url
          });
        }
      },
      
      new_conversation_message: () => {
        fetchConversations();
      },
      
      message_edited: ({ message }) => {
        setMessages(prev => prev.map(m => m._id === message._id ? message : m));
      },
      
      message_deleted: ({ messageId }) => {
        setMessages(prev => prev.map(m => 
          m._id === messageId ? { ...m, isDeleted: true, content: 'This message was deleted' } : m
        ));
      },
      
      message_reaction: ({ message }) => {
        setMessages(prev => prev.map(m => m._id === message._id ? message : m));
      },
      
      user_typing: ({ userId, isTyping }) => {
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          if (isTyping) {
            newSet.add(userId);
          } else {
            newSet.delete(userId);
          }
          return newSet;
        });
      },
      
      messages_read: ({ messageIds }) => {
        setMessages(prev => prev.map(m => 
          messageIds.includes(m._id) ? { ...m, isRead: true } : m
        ));
      },
      
      group_updated: ({ conversation }) => {
        // Update the selected conversation if it's the one that was updated
        if (selectedConversation?._id === conversation._id) {
          setSelectedConversation(conversation);
        }
        fetchConversations();
      },
      
      group_deleted: ({ conversationId }) => {
        // If the deleted group is currently selected, clear selection
        if (selectedConversation?._id === conversationId) {
          setSelectedConversation(null);
          setMessages([]);
        }
        // Remove from conversations list
        setConversations(prev => prev.filter(c => c._id !== conversationId));
      }
    };

    // Register all event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      newSocket.on(event, handler);
    });

    socketListenersRef.current = handlers;
    setSocket(newSocket);

    return () => {
      // Cleanup all event listeners
      Object.keys(handlers).forEach(event => {
        newSocket.off(event);
      });
      newSocket.close();
    };
  }, [isAuthenticated, user]);

  // Join conversation room when selected
  useEffect(() => {
    if (socket && selectedConversation) {
      socket.emit('join_conversation', selectedConversation._id);
      
      return () => {
        socket.emit('leave_conversation', selectedConversation._id);
      };
    }
  }, [socket, selectedConversation]);

  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`/api/messages/conversations?includeArchived=${includeArchived}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch conversations');
      
      const data = await response.json();
      
      if (data.success) {
        setConversations(data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  const startConversation = useCallback(async (userId) => {
    if (!userId) return;
    
    try {
      const response = await fetch(`/api/messages/conversations/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to start conversation');
      
      const data = await response.json();
      
      if (data.success) {
        setSelectedConversation(data.data);
        if (isMobile) {
          setShowConversationsList(false);
        }
        await fetchConversations();
        setError(null);
      }
    } catch (err) {
      console.error('Failed to start conversation:', err);
      setError('Failed to start conversation');
    }
  }, [fetchConversations, isMobile]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchConversations();
      
      const userId = searchParams.get('userId');
      if (userId) {
        startConversation(userId);
      }
    }
  }, [isAuthenticated, searchParams, fetchConversations, startConversation]);

  const fetchMessages = useCallback(async (conversationId, page = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    }
    
    try {
      const response = await fetch(
        `/api/messages/conversations/${conversationId}/messages?limit=50&page=${page}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch messages');
      
      const data = await response.json();
      
      if (data.success) {
        if (page === 1) {
          setMessages(data.data.messages);
          setTimeout(() => scrollToBottom(false), 100);
        } else {
          // Save scroll position
          const container = messagesContainerRef.current;
          const oldScrollHeight = container?.scrollHeight || 0;
          
          setMessages(prev => [...data.data.messages, ...prev]);
          
          // Restore scroll position
          setTimeout(() => {
            if (container) {
              const newScrollHeight = container.scrollHeight;
              container.scrollTop = newScrollHeight - oldScrollHeight;
            }
          }, 100);
        }
        setHasMoreMessages(data.data.currentPage < data.data.totalPages);
        setCurrentPage(page);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoadingMore(false);
    }
  }, [scrollToBottom]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id, 1);
    }
  }, [selectedConversation, fetchMessages]);

  const handleTyping = useCallback((isTyping) => {
    if (socket && selectedConversation) {
      socket.emit('typing', {
        conversationId: selectedConversation._id,
        isTyping
      });
    }
  }, [socket, selectedConversation]);

  // Detect and fetch link previews
  useEffect(() => {
    const detectLinks = async () => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const urls = newMessage.match(urlRegex);
      
      if (urls && urls.length > 0) {
        const url = urls[0]; // Get first URL
        if (!linkPreviews[url]) {
          try {
            const response = await fetch(`/api/messages/link-preview?url=${encodeURIComponent(url)}`, {
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });
            
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                setLinkPreviews(prev => ({ ...prev, [url]: data.data }));
              }
            }
          } catch (err) {
            console.error('Failed to fetch link preview:', err);
          }
        }
      }
    };
    
    if (newMessage) {
      detectLinks();
    }
  }, [newMessage, linkPreviews]);
  
  const handleMentionSelect = (user) => {
    // Replace the @query with @username
    const lastAtIndex = newMessage.lastIndexOf('@');
    const before = newMessage.substring(0, lastAtIndex);
    const after = ' '; // Add space after mention
    setNewMessage(before + `@${user.username}${after}`);
  };
  
  const sendMessage = async (e) => {
    e.preventDefault();
    
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;
    
    setSendingMessage(true);
    setError(null);
    
    try {
      const otherUser = getOtherUser(selectedConversation);
      const messageText = newMessage.trim().toLowerCase();
      
      // Check for claim message
      const claimPattern = /^claim(?:\s+([a-zA-Z0-9-]+))?(?:\s+(\d+))?$/i;
      const claimMatch = messageText.match(claimPattern);
      const isClaimMessage = claimMatch && 
                             otherUser?.username === 'KeyDistributor' && 
                             otherUser?.isBot;
      
      let giveawayId = null;
      let position = null;
      
      if (claimMatch) {
        giveawayId = claimMatch[1] || null;
        position = claimMatch[2] ? parseInt(claimMatch[2]) : null;
      }
      
      if (isClaimMessage) {
        // Handle claim message
        const sendResponse = await fetch(
          `/api/messages/conversations/${selectedConversation._id}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ content: newMessage.trim() })
          }
        );

        if (!sendResponse.ok) {
          throw new Error('Failed to send claim message');
        }

        const claimResponse = await fetch(
          `/api/messages/conversations/${selectedConversation._id}/claim`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ giveawayId, position })
          }
        );
        
        if (!claimResponse.ok) {
          const errorData = await claimResponse.json();
          throw new Error(errorData.message || 'Failed to claim key');
        }
        
        setNewMessage('');
        setReplyingTo(null);
        await fetchMessages(selectedConversation._id);
        await fetchConversations();
      } else if (selectedFile) {
        // Handle file upload
        const formData = new FormData();
        formData.append('file', selectedFile);
        if (newMessage.trim()) {
          formData.append('content', newMessage.trim());
        }

        const response = await fetch(
          `/api/messages/conversations/${selectedConversation._id}/attachments`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: formData
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to upload file');
        }
        
        setNewMessage('');
        setSelectedFile(null);
        setReplyingTo(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await fetchMessages(selectedConversation._id);
        await fetchConversations();
      } else {
        // Regular message
        const body = { content: newMessage.trim() };
        if (replyingTo) {
          body.replyTo = replyingTo._id;
        }

        const response = await fetch(
          `/api/messages/conversations/${selectedConversation._id}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(body)
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to send message');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setNewMessage('');
          setReplyingTo(null);
          handleTyping(false);
          await fetchMessages(selectedConversation._id);
          await fetchConversations();
        }
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const editMessage = async (messageId, newContent) => {
    try {
      const response = await fetch(`/api/messages/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ content: newContent })
      });

      if (!response.ok) {
        throw new Error('Failed to edit message');
      }
      
      setEditingMessage(null);
      setError(null);
    } catch (err) {
      console.error('Failed to edit message:', err);
      setError('Failed to edit message');
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;

    try {
      const response = await fetch(`/api/messages/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete message');
      }
      
      await fetchMessages(selectedConversation._id);
      setError(null);
    } catch (err) {
      console.error('Failed to delete message:', err);
      setError('Failed to delete message');
    }
  };

  const addReaction = async (messageId, emoji) => {
    try {
      const response = await fetch(`/api/messages/messages/${messageId}/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ emoji })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add reaction');
      }
      
      setEmojiPickerOpen(null);
      setError(null);
    } catch (err) {
      console.error('Failed to add reaction:', err);
      setError('Failed to add reaction');
    }
  };

  const searchMessages = async () => {
    if (!searchQuery.trim()) return;

    try {
      const response = await fetch(
        `/api/messages/search?query=${encodeURIComponent(searchQuery)}${selectedConversation ? `&conversationId=${selectedConversation._id}` : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to search messages');
      }
      
      const data = await response.json();

      if (data.success) {
        setSearchResults(data.data);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to search messages:', err);
      setError('Failed to search messages');
    }
  };

  const toggleArchive = async (conversationId) => {
    try {
      const response = await fetch(`/api/messages/conversations/${conversationId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to archive conversation');
      }
      
      await fetchConversations();
      setError(null);
    } catch (err) {
      console.error('Failed to archive conversation:', err);
      setError('Failed to archive conversation');
    }
  };

  const togglePin = async (conversationId) => {
    try {
      const response = await fetch(`/api/messages/conversations/${conversationId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to pin conversation');
      }
      
      await fetchConversations();
      setError(null);
    } catch (err) {
      console.error('Failed to pin conversation:', err);
      setError('Failed to pin conversation');
    }
  };

  const blockUser = async (userId) => {
    if (!window.confirm('Block this user? You won\'t be able to send or receive messages from them.')) return;

    try {
      const response = await fetch(`/api/messages/users/${userId}/block`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to block user');
      }
      
      alert('User blocked successfully');
      setSelectedConversation(null);
      if (isMobile) {
        setShowConversationsList(true);
      }
      await fetchConversations();
      setError(null);
    } catch (err) {
      console.error('Failed to block user:', err);
      setError('Failed to block user');
    }
  };

  const formatTime = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (messageDate.toDateString() === today.toDateString()) {
      return messageDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  const getOtherUser = useCallback((conversation) => {
    if (!conversation || !user) return null;
    return conversation.participants.find(p => p._id !== user._id);
  }, [user]);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  // Get all unique labels from conversations
  const getLabelCounts = useMemo(() => {
    const labelMap = new Map();
    
    conversations.forEach(conv => {
      if (conv.labels && conv.labels.length > 0) {
        conv.labels.forEach(label => {
          const labelKey = label.label === 'custom' ? label.customLabelName : label.label;
          const labelData = labelMap.get(labelKey) || { 
            count: 0, 
            color: label.color,
            originalLabel: label.label,
            customName: label.customLabelName
          };
          labelData.count += 1;
          labelMap.set(labelKey, labelData);
        });
      }
    });
    
    return labelMap;
  }, [conversations]);

  // Filter conversations by selected label
  const filteredConversations = useMemo(() => {
    if (!selectedLabelFilter) {
      return conversations;
    }
    
    return conversations.filter(conv => {
      if (!conv.labels || conv.labels.length === 0) return false;
      
      return conv.labels.some(label => {
        const labelKey = label.label === 'custom' ? label.customLabelName : label.label;
        return labelKey === selectedLabelFilter;
      });
    });
  }, [conversations, selectedLabelFilter]);

  // Avatar component with fallback to initial
  const Avatar = React.memo(({ user, size = 32 }) => {
    const [imgError, setImgError] = useState(false);
    
    const colors = useMemo(() => [
      '#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6',
      '#1abc9c', '#e67e22', '#34495e', '#16a085', '#c0392b'
    ], []);
    
    const getColorFromUsername = useCallback((username) => {
      if (!username) return colors[0];
      const charCode = username.charCodeAt(0);
      return colors[charCode % colors.length];
    }, [colors]);

    const initial = user?.username?.[0]?.toUpperCase() || 'U';
    const bgColor = getColorFromUsername(user?.username);

    useEffect(() => {
      setImgError(false);
    }, [user?.profile?.avatar?.url]);

    if (user?.profile?.avatar?.url && !imgError) {
      return (
        <img
          src={user.profile.avatar.url}
          alt={user.username}
          className="avatar-image"
          style={{
            width: `${size}px`,
            height: `${size}px`
          }}
          onError={() => setImgError(true)}
        />
      );
    }

    return (
      <div 
        className="avatar-placeholder"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          background: bgColor,
          fontSize: `${size * 0.45}px`
        }}
      >
        {initial}
      </div>
    );
  });

  const loadMoreMessages = useCallback(() => {
    if (hasMoreMessages && selectedConversation && !loadingMore) {
      fetchMessages(selectedConversation._id, currentPage + 1, true);
    }
  }, [hasMoreMessages, selectedConversation, loadingMore, currentPage, fetchMessages]);

  const handleBackToConversations = () => {
    setSelectedConversation(null);
    setShowConversationsList(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="messages-container">
        <div className="messages-card messages-empty">
          <h2>🔒 Login Required</h2>
          <p>Please log in to access messages</p>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="messages-header">
        <h1>💬 Messages</h1>
        <div className="messages-header-actions">
          <button
            onClick={() => setShowCreateGroup(true)}
            className="btn-primary"
            title="Create Group Chat"
          >
            👥 New Group
          </button>
          <button
            onClick={() => setShowSearch(!showSearch)}
            className="btn-icon"
            aria-label={showSearch ? 'Close Search' : 'Search Messages'}
          >
            {showSearch ? '✕' : '🔍'}
          </button>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <span>Show Archived</span>
          </label>
        </div>
      </div>

      {error && (
        <div className="messages-error" role="alert">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error">✕</button>
        </div>
      )}

      {showSearch && (
        <div className="messages-search-panel">
          <div className="search-input-group">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchMessages()}
              placeholder="Search messages..."
              className="search-input"
            />
            <button onClick={searchMessages} className="btn-primary">
              Search
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map(msg => (
                <div
                  key={msg._id}
                  className="search-result-item"
                  onClick={() => {
                    setSelectedConversation(msg.conversationId);
                    setShowSearch(false);
                    if (isMobile) {
                      setShowConversationsList(false);
                    }
                  }}
                >
                  <div className="search-result-user">{msg.senderId.username}</div>
                  <div className="search-result-content">{msg.content}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      <div className={`messages-layout ${isMobile ? 'mobile' : 'desktop'} ${selectedConversation && !isMobile ? 'with-conversation' : ''}`}>
        {/* Conversations List */}
        {(showConversationsList || !isMobile) && (
          <div className="conversations-panel">
            <div className="conversations-header">
              <span className="panel-icon">💬</span>
              <span>Messages</span>
            </div>
            
            {/* Label Folders */}
            {getLabelCounts.size > 0 && (
              <div className="label-folders">
                <button
                  className={`label-folder ${!selectedLabelFilter ? 'active' : ''}`}
                  onClick={() => setSelectedLabelFilter(null)}
                >
                  <span className="folder-icon">📁</span>
                  <span className="folder-name">All</span>
                  <span className="folder-count">{conversations.length}</span>
                </button>
                {Array.from(getLabelCounts.entries()).map(([labelKey, data]) => (
                  <button
                    key={labelKey}
                    className={`label-folder ${selectedLabelFilter === labelKey ? 'active' : ''}`}
                    onClick={() => setSelectedLabelFilter(labelKey)}
                    style={{ '--label-color': data.color }}
                  >
                    <span className="folder-icon" style={{ color: data.color }}>🏷️</span>
                    <span className="folder-name">{labelKey}</span>
                    <span className="folder-count">{data.count}</span>
                  </button>
                ))}
              </div>
            )}
            
            <div className="conversations-list">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading conversations...</p>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="empty-state">
                  {selectedLabelFilter ? (
                    <>
                      <p>No conversations with "{selectedLabelFilter}" label</p>
                      <button 
                        onClick={() => setSelectedLabelFilter(null)}
                        className="btn-secondary"
                      >
                        Show All Conversations
                      </button>
                    </>
                  ) : (
                    <>
                      <p>No conversations yet</p>
                      <p className="empty-hint">Visit a user's profile to start chatting!</p>
                    </>
                  )}
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const otherUser = getOtherUser(conv);
                  if (!conv.isGroup && !otherUser) return null;
                  
                  return (
                    <div
                      key={conv._id}
                      onClick={() => {
                        setSelectedConversation(conv);
                        if (isMobile) {
                          setShowConversationsList(false);
                        }
                      }}
                      className={`conversation-item ${selectedConversation?._id === conv._id ? 'active' : ''}`}
                    >
                      <div className="conversation-avatar-wrapper">
                        <div className={`conversation-avatar ${selectedConversation?._id === conv._id ? 'active' : ''}`}>
                          {conv.isGroup ? (
                            conv.groupAvatar ? (
                              <img src={conv.groupAvatar} alt={conv.groupName} />
                            ) : (
                              <div className="group-avatar-placeholder">👥</div>
                            )
                          ) : (
                            <Avatar user={otherUser} size={isMobile ? 44 : 50} />
                          )}
                        </div>
                        {!conv.isGroup && isUserOnline(otherUser._id) && (
                          <span className="online-indicator" title="Online" />
                        )}
                        {conv.unreadCount > 0 && (
                          <span className="unread-badge">
                            {conv.unreadCount > 9 ? '9+' : conv.unreadCount}
                          </span>
                        )}
                      </div>
                      
                      <div className="conversation-info">
                        <div className="conversation-top">
                          <span className="conversation-username">
                            {conv.isPinned && '📌'}
                            {conv.isGroup ? '👥 ' : ''}
                            {conv.isGroup ? conv.groupName : otherUser.username}
                          </span>
                          {conv.lastMessageAt && (
                            <span className="conversation-time">
                              {formatTime(conv.lastMessageAt)}
                            </span>
                          )}
                        </div>
                        {conv.lastMessage && (
                          <div className="conversation-preview">
                            {conv.lastMessage.content}
                          </div>
                        )}
                        {conv.labels && conv.labels.length > 0 && (
                          <div className="conversation-labels">
                            {conv.labels.slice(0, 3).map((label) => (
                              <span 
                                key={label._id} 
                                className="label-chip-small"
                                style={{ 
                                  background: `${label.color}20`, 
                                  borderColor: label.color,
                                  color: label.color
                                }}
                              >
                                {label.label === 'custom' ? label.customLabelName : label.label}
                              </span>
                            ))}
                            {conv.labels.length > 3 && (
                              <span className="label-more">+{conv.labels.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {conv.isArchived && (
                        <span className="archived-icon">📦</span>
                      )}
                      
                      <button
                        className="quick-label-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowLabelsModal(conv._id);
                        }}
                        title="Manage labels"
                      >
                        🏷️
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Messages View */}
        {selectedConversation && (!isMobile || !showConversationsList) && (
          <div className="messages-panel">
            {/* Header */}
            <div className="messages-panel-header">
              {isMobile && (
                <button 
                  className="back-button"
                  onClick={handleBackToConversations}
                  aria-label="Back to conversations"
                >
                  ←
                </button>
              )}
              <div className="header-user-info">
                <div className="header-avatar">
                  {selectedConversation.isGroup ? (
                    selectedConversation.groupAvatar ? (
                      <img src={selectedConversation.groupAvatar} alt={selectedConversation.groupName} />
                    ) : (
                      <div className="group-avatar">👥</div>
                    )
                  ) : (
                    <Avatar user={getOtherUser(selectedConversation)} size={isMobile ? 36 : 44} />
                  )}
                </div>
                <div>
                  <div className="header-username">
                    {selectedConversation.isGroup ? selectedConversation.groupName : getOtherUser(selectedConversation)?.username}
                  </div>
                  {!selectedConversation.isGroup && (
                    <div className="header-status">
                      {isUserOnline(getOtherUser(selectedConversation)?._id) ? (
                        <>
                          <span className="status-dot online" />
                          <span className="status-text online">Active now</span>
                        </>
                      ) : (
                        <>
                          <span className="status-dot offline" />
                          <span className="status-text">Offline</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="header-actions">
                {selectedConversation.isGroup && (
                  <button
                    onClick={() => setShowGroupManagement(true)}
                    className="action-button"
                    title="Group Settings"
                  >
                    ⚙️
                  </button>
                )}
                <button
                  onClick={() => setShowScheduledPicker(true)}
                  className="action-button"
                  title="Schedule Message"
                >
                  📅
                </button>
                <button
                  onClick={() => setShowLabelsModal(selectedConversation._id)}
                  className="action-button"
                  title="Organize Labels"
                >
                  🏷️
                </button>
                <button
                  onClick={() => togglePin(selectedConversation._id)}
                  className="action-button"
                  title={selectedConversation.isPinned ? 'Unpin' : 'Pin'}
                  aria-label={selectedConversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
                >
                  {selectedConversation.isPinned ? '📌' : '📍'}
                </button>
                <button
                  onClick={() => toggleArchive(selectedConversation._id)}
                  className="action-button"
                  title={selectedConversation.isArchived ? 'Unarchive' : 'Archive'}
                  aria-label={selectedConversation.isArchived ? 'Unarchive conversation' : 'Archive conversation'}
                >
                  📦
                </button>
                {!isMobile && (
                  <>
                    <button
                      onClick={() => blockUser(getOtherUser(selectedConversation)?._id)}
                      className="action-button danger"
                      title="Block User"
                      aria-label="Block user"
                    >
                      🚫
                    </button>
                    <button
                      onClick={handleBackToConversations}
                      className="action-button danger"
                      title="Close"
                      aria-label="Close conversation"
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="messages-list" ref={messagesContainerRef}>
              {loadingMore && (
                <div className="loading-more">
                  <div className="spinner small"></div>
                  <span>Loading more messages...</span>
                </div>
              )}
              
              {hasMoreMessages && !loadingMore && (
                <button
                  onClick={loadMoreMessages}
                  className="load-more-button"
                >
                  ⬆️ Load More Messages
                </button>
              )}
              
              {messages.length === 0 ? (
                <div className="empty-messages">
                  <div className="empty-icon">💬</div>
                  <div className="empty-title">No messages yet</div>
                  <div className="empty-subtitle">
                    {selectedConversation.isGroup ? (
                      <>
                        Start chatting with {selectedConversation.groupName || 'this group'}!
                        <div style={{ marginTop: '8px', fontSize: '14px', opacity: 0.7 }}>
                          {selectedConversation.participants?.length} members
                        </div>
                      </>
                    ) : (
                      <>Start the conversation with {getOtherUser(selectedConversation)?.username}!</>
                    )}
                  </div>
                </div>
              ) : (
                messages.map((msg) => {
                  const isSent = msg.senderId._id === user._id;
                  
                  return (
                    <div key={msg._id} className="message-wrapper">
                      {msg.replyTo && (
                        <div className={`message-reply ${isSent ? 'sent' : 'received'}`}>
                          <div className="reply-label">
                            ↩️ Reply to {msg.replyTo.senderId?.username || 'message'}
                          </div>
                          <div className="reply-content">
                            {msg.replyTo.content?.substring(0, 60)}{msg.replyTo.content?.length > 60 ? '...' : ''}
                          </div>
                        </div>
                      )}
                      
                      <div
                        className={`message ${isSent ? 'sent' : 'received'}`}
                        onMouseEnter={() => setMessageMenuOpen(msg._id)}
                        onMouseLeave={() => setMessageMenuOpen(null)}
                        onClick={() => {
                          if (isMobile) {
                            setMessageMenuOpen(messageMenuOpen === msg._id ? null : msg._id);
                          }
                        }}
                      >
                        {!isSent && (
                          <div className="message-avatar">
                            <Avatar user={msg.senderId} size={32} />
                          </div>
                        )}
                        
                        <div className="message-content-wrapper">
                          {!isSent && (
                            <span className="message-sender">
                              {msg.senderId.username}
                            </span>
                          )}
                          
                          <div className={`message-bubble ${msg.isDeleted ? 'deleted' : ''}`}>
                            {msg.attachments && msg.attachments.length > 0 && (
                              <div className="message-attachments">
                                {msg.attachments.map((att, idx) => (
                                  <div key={idx} className="attachment">
                                    {att.type === 'image' && (
                                      <img 
                                        src={att.url} 
                                        alt={att.filename}
                                        className="attachment-image"
                                      />
                                    )}
                                    {att.type === 'video' && (
                                      <video 
                                        src={att.url} 
                                        controls
                                        className="attachment-video"
                                      />
                                    )}
                                    {att.type === 'voice' && (
                                      <VoicePlayer audioUrl={att.url} />
                                    )}
                                    {att.type === 'file' && (
                                      <a href={att.url} target="_blank" rel="noopener noreferrer" className="attachment-file">
                                        📎 {att.filename}
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {msg.linkPreview && (
                              <LinkPreview preview={msg.linkPreview} />
                            )}
                            
                            {editingMessage?._id === msg._id ? (
                              <form 
                                onSubmit={(e) => {
                                  e.preventDefault();
                                  editMessage(msg._id, e.target.content.value);
                                }}
                                className="edit-form"
                              >
                                <div className="edit-label">
                                  ✏️ EDITING MESSAGE
                                </div>
                                <textarea
                                  name="content"
                                  defaultValue={msg.content}
                                  autoFocus
                                  rows="3"
                                  className="edit-textarea"
                                />
                                <div className="edit-actions">
                                  <button type="submit" className="btn-primary btn-small">
                                    ✓ Save
                                  </button>
                                  <button 
                                    type="button" 
                                    onClick={() => setEditingMessage(null)}
                                    className="btn-secondary btn-small"
                                  >
                                    ✕ Cancel
                                  </button>
                                </div>
                              </form>
                            ) : (
                              <div className="message-text">{msg.content}</div>
                            )}
                            
                            <div className="message-meta">
                              <span>
                                {msg.isEdited && '(edited) '}
                                {formatTime(msg.createdAt)}
                              </span>
                              {isSent && msg.isRead && <span className="read-receipt">✓✓</span>}
                            </div>

                            {msg.reactions && msg.reactions.length > 0 && (
                              <div className="message-reactions">
                                {Object.entries(
                                  msg.reactions.reduce((acc, r) => {
                                    acc[r.emoji] = (acc[r.emoji] || 0) + 1;
                                    return acc;
                                  }, {})
                                ).map(([emoji, count]) => (
                                  <span
                                    key={emoji}
                                    className="reaction"
                                    onClick={() => addReaction(msg._id, emoji)}
                                  >
                                    {emoji} {count}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Message actions menu */}
                        {!msg.isDeleted && (
                          <div className={`message-actions ${isSent ? 'sent' : 'received'} ${(messageMenuOpen === msg._id || (!isMobile && messageMenuOpen === msg._id)) ? 'show' : ''}`}>
                            <button
                              onClick={() => setReplyingTo(msg)}
                              className="action-btn"
                              title="Reply"
                              aria-label="Reply to message"
                            >
                              ↩️
                            </button>
                            <button
                              onClick={() => setEmojiPickerOpen(msg._id)}
                              className="action-btn"
                              title="React"
                              aria-label="React to message"
                            >
                              😊
                            </button>
                            {isSent && (
                              <>
                                <button
                                  onClick={() => setEditingMessage(msg)}
                                  className="action-btn"
                                  title="Edit"
                                  aria-label="Edit message"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => deleteMessage(msg._id)}
                                  className="action-btn danger"
                                  title="Delete"
                                  aria-label="Delete message"
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        )}

                        {/* Emoji picker */}
                        {emojiPickerOpen === msg._id && (
                          <div className={`emoji-picker ${isSent ? 'sent' : 'received'}`}>
                            {commonEmojis.map(emoji => (
                              <button
                                key={emoji}
                                onClick={() => {
                                  addReaction(msg._id, emoji);
                                  setEmojiPickerOpen(null);
                                }}
                                className="emoji-btn"
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              
              {typingUsers.size > 0 && (
                <div className="typing-indicator">
                  <Avatar user={getOtherUser(selectedConversation)} size={32} />
                  <div className="typing-dots">
                    <span className="dot" style={{ animationDelay: '0s' }} />
                    <span className="dot" style={{ animationDelay: '0.2s' }} />
                    <span className="dot" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="messages-input-container">
              {showVoiceRecorder && (
                <VoiceRecorder
                  onSend={async (audioBlob) => {
                    try {
                      const formData = new FormData();
                      formData.append('voice', audioBlob, 'voice-message.webm');
                      
                      const response = await fetch(
                        `/api/messages/conversations/${selectedConversation._id}/voice`,
                        {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                          },
                          body: formData
                        }
                      );
                      
                      if (response.ok) {
                        await fetchMessages(selectedConversation._id);
                        setShowVoiceRecorder(false);
                      } else {
                        throw new Error('Failed to send voice message');
                      }
                    } catch (err) {
                      setError('Failed to send voice message');
                    }
                  }}
                  onCancel={() => setShowVoiceRecorder(false)}
                />
              )}
              
              {replyingTo && (
                <div className="reply-preview">
                  <div className="reply-preview-content">
                    <div className="reply-preview-label">
                      ↩️ REPLYING TO {replyingTo.senderId.username.toUpperCase()}
                    </div>
                    <div className="reply-preview-text">
                      {replyingTo.content.substring(0, 80)}{replyingTo.content.length > 80 ? '...' : ''}
                    </div>
                  </div>
                  <button
                    onClick={() => setReplyingTo(null)}
                    className="reply-cancel"
                    aria-label="Cancel reply"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              {selectedFile && (
                <div className="file-preview">
                  <div className="file-preview-info">
                    <span className="file-icon">📎</span>
                    <div>
                      <div className="file-name">{selectedFile.name}</div>
                      <div className="file-size">
                        {(selectedFile.size / 1024).toFixed(2)} KB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="file-cancel"
                    aria-label="Remove file"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <MentionAutocomplete
                value={newMessage}
                onSelect={handleMentionSelect}
                participants={selectedConversation.participants || []}
              />
              
              <form onSubmit={sendMessage} className="message-input-form">
                <input
                  type="file"
                  ref={fileInputRef}
                  className="file-input-hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // Validate file size (10MB max)
                      if (file.size > 10 * 1024 * 1024) {
                        setError('File size must be less than 10MB');
                        return;
                      }
                      setSelectedFile(file);
                    }
                  }}
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="attach-button"
                  aria-label="Attach file"
                >
                  📎
                </button>
                <button
                  type="button"
                  onClick={() => setShowVoiceRecorder(!showVoiceRecorder)}
                  className="attach-button"
                  aria-label="Record voice message"
                  title="Voice Message"
                >
                  🎤
                </button>
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping(e.target.value.length > 0);
                    
                    if (typingTimeoutRef.current) {
                      clearTimeout(typingTimeoutRef.current);
                    }
                    
                    typingTimeoutRef.current = setTimeout(() => {
                      handleTyping(false);
                    }, 1000);
                  }}
                  placeholder="Type a message..."
                  className="message-input"
                  disabled={sendingMessage}
                  aria-label="Type a message"
                />
                <button
                  type="submit"
                  className="send-button"
                  disabled={sendingMessage || (!newMessage.trim() && !selectedFile)}
                  aria-label="Send message"
                >
                  {sendingMessage ? '⏳' : '✈️'}
                  {!isMobile && <span className="send-text">Send</span>}
                </button>
              </form>
            </div>
          </div>
        )}
        
        {/* Empty state when no conversation selected on desktop */}
        {!selectedConversation && !isMobile && (
          <div className="no-conversation-selected">
            <div className="empty-icon">💬</div>
            <h2>Select a conversation</h2>
            <p>Choose a conversation from the list to start messaging</p>
          </div>
        )}
      </div>
      
      {/* Modals */}
      {showCreateGroup && (
        <CreateGroupModal
          onClose={() => setShowCreateGroup(false)}
          onCreated={(group) => {
            setShowCreateGroup(false);
            setSelectedConversation(group);
            fetchConversations();
            if (isMobile) {
              setShowConversationsList(false);
            }
          }}
        />
      )}
      
      {showGroupManagement && selectedConversation && (
        <GroupManagement
          conversation={selectedConversation}
          onClose={() => setShowGroupManagement(false)}
          onUpdate={(updatedConv) => {
            if (updatedConv === null) {
              // Group was deleted or user left
              setSelectedConversation(null);
              setShowGroupManagement(false);
            } else {
              setSelectedConversation(updatedConv);
            }
            fetchConversations();
          }}
        />
      )}
      
      {showLabelsModal && (
        <ConversationLabels
          conversationId={showLabelsModal}
          onClose={() => setShowLabelsModal(null)}
          onUpdate={() => {
            fetchConversations();
          }}
        />
      )}
      
      {showScheduledPicker && selectedConversation && (
        <ScheduledMessagePicker
          conversationId={selectedConversation._id}
          onClose={() => setShowScheduledPicker(false)}
          onScheduled={() => {
            // Optional: Show confirmation
          }}
        />
      )}
    </div>
  );
};

export default Messages;
